import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { Sparkline } from '@/components/charts/Sparkline';
import { EmptyState } from '@/components/charts/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useOutletRanking, type OutletRanking } from '@/hooks/useOutletRanking';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

type SortKey = 'name' | 'revenue' | 'revenue_delta' | 'orders' | 'orders_delta';
type SortDir = 'asc' | 'desc';

export function OutletsPage() {
  const { period } = useDashboard();
  const revenue = useOutletRanking({ source: 'net_sales', period });
  const orders = useOutletRanking({ source: 'orders', period });
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => {
    if (!revenue.data || !orders.data) return [];
    const ordersById = new Map(orders.data.rankings.map((r) => [r.outletId, r]));
    return revenue.data.rankings.map((r) => ({
      outletId: r.outletId,
      outletName: r.outletName,
      city: r.city,
      revenue: r,
      orders: ordersById.get(r.outletId)!,
    }));
  }, [revenue.data, orders.data]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'name':
          return a.outletName.localeCompare(b.outletName) * dir;
        case 'revenue':
          return (a.revenue.metric.current - b.revenue.metric.current) * dir;
        case 'revenue_delta':
          return (a.revenue.metric.deltaPct - b.revenue.metric.deltaPct) * dir;
        case 'orders':
          return (a.orders.metric.current - b.orders.metric.current) * dir;
        case 'orders_delta':
          return (a.orders.metric.deltaPct - b.orders.metric.deltaPct) * dir;
      }
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const toggle = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };

  const loading = revenue.isLoading || orders.isLoading;
  const range = revenue.data?.current;
  const compareRange = revenue.data?.previous;

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            Outlets
          </span>
        }
        description={
          range && compareRange
            ? `${range.label} · ${fmtISTRange(range.start, range.end)} vs ${fmtISTRange(compareRange.start, compareRange.end)}`
            : 'All Flax outlets ranked by selected metric.'
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-6">
              <EmptyState title="No outlets" description="Seed outlets in marketing.outlets to populate." />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                  <tr>
                    <Th label="Outlet" onClick={() => toggle('name')} active={sortKey === 'name'} dir={sortDir} />
                    <Th label="Revenue" onClick={() => toggle('revenue')} active={sortKey === 'revenue'} dir={sortDir} align="right" />
                    <Th label="vs prev" onClick={() => toggle('revenue_delta')} active={sortKey === 'revenue_delta'} dir={sortDir} align="right" />
                    <th className="px-3 py-2 text-left font-medium">Trend</th>
                    <Th label="Orders" onClick={() => toggle('orders')} active={sortKey === 'orders'} dir={sortDir} align="right" />
                    <Th label="vs prev" onClick={() => toggle('orders_delta')} active={sortKey === 'orders_delta'} dir={sortDir} align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {sorted.map((row) => (
                    <Row key={row.outletId} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Th({
  label,
  onClick,
  active,
  dir,
  align = 'left',
}: {
  label: string;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  align?: 'left' | 'right';
}) {
  return (
    <th className={cn('px-3 py-2 font-medium', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          active && 'text-foreground',
        )}
      >
        {label}
        <ArrowUpDown
          className={cn(
            'h-3 w-3 opacity-50',
            active && 'opacity-100',
            active && dir === 'asc' && 'rotate-180',
          )}
        />
      </button>
    </th>
  );
}

function Row({
  row,
}: {
  row: {
    outletId: string;
    outletName: string;
    city: string;
    revenue: OutletRanking;
    orders: OutletRanking;
  };
}) {
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-3 py-3">
        <Link to={`/outlets/${row.outletId}`} className="font-medium hover:underline">
          {row.outletName}
        </Link>
        <Badge variant="muted" className="ml-2 text-2xs">
          {row.city}
        </Badge>
      </td>
      <td className="px-3 py-3 text-right font-mono tabular-nums">
        {formatINRCompact(row.revenue.metric.current)}
      </td>
      <td className="px-3 py-3 text-right">
        <DeltaBadge
          deltaPct={row.revenue.metric.deltaPct}
          comparable={row.revenue.metric.comparable}
        />
      </td>
      <td className="px-3 py-3">
        <Sparkline
          data={row.revenue.trend}
          width={120}
          height={28}
          tone={row.revenue.metric.deltaPct >= 0 ? 'positive' : 'negative'}
        />
      </td>
      <td className="px-3 py-3 text-right font-mono tabular-nums">
        {formatNumberCompact(row.orders.metric.current)}
      </td>
      <td className="px-3 py-3 text-right">
        <DeltaBadge
          deltaPct={row.orders.metric.deltaPct}
          comparable={row.orders.metric.comparable}
        />
      </td>
    </tr>
  );
}
